import { req } from "./transport";

export const paymentsApi = {
  // Increment 34: platform fees (admin)
  async adminFees() {
    return req<{ feePct: number; totalCollected: number; recent: any[] }>("/admin/fees");
  },

  async adminSetFee(pct: number) {
    return req<{ ok: boolean; feePct: number }>("/admin/fees", { method: "POST", body: JSON.stringify({ pct }) });
  },

  // v3.9.64 (Stage 6 / A5) — supplier commission config
  async adminSupplierCommission() {
    return req<{ config: { enabled: boolean; pct: number }; collected: number }>("/admin/supplier-commission");
  },

  async adminSetSupplierCommission(cfg: { enabled?: boolean; pct?: number }) {
    return req<{ ok: boolean; config: { enabled: boolean; pct: number } }>("/admin/supplier-commission", { method: "POST", body: JSON.stringify(cfg) });
  },

  // v3.9.61 (Stage 5 / A1) — client protection fee config
  async adminProtectionFee() {
    return req<{ config: any; collected: number; count: number }>("/admin/protection-fee");
  },

  async adminSetProtectionFee(cfg: any) {
    return req<{ ok: boolean; config: any }>("/admin/protection-fee", { method: "POST", body: JSON.stringify(cfg) });
  },

  // v3.9.33 — posting subscription / paywall config
  async adminPosting() {
    return req<any>("/admin/posting");
  },

  async adminSetPosting(body: { enabled?: boolean; perPost?: number; subscription?: number; subscriptionDays?: number; freePerCategory?: number }) {
    return req<any>("/admin/posting", { method: "POST", body: JSON.stringify(body) });
  },

  // v3.9.33 — customer posting entitlement (used by the create-post paywall in 2b)
  async billingStatus(type?: string, category?: string) {
    const qs = new URLSearchParams();
    if (type) qs.set("type", type);
    if (category) qs.set("category", category);
    const s = qs.toString();
    return req<any>(`/billing/status${s ? `?${s}` : ""}`);
  },

  async billingPay(body: { kind: "subscription" | "post_credit"; method?: "qrph" | "card"; postType?: string; category?: string; mobile?: string }) {
    return req<any>("/billing/pay", { method: "POST", body: JSON.stringify(body) });
  },

  // v3.9.41 (Stage 1 / A8) — plan catalog
  async getPlans(audience?: string) {
    return req<{ plans: any[]; paidPlansAvailable: boolean }>(`/plans${audience ? `?audience=${encodeURIComponent(audience)}` : ""}`);
  },

  async paidPlansAvailability() { return req<{ paidPlansAvailable: boolean }>("/plans/availability"); },

  async adminPlans() { return req<{ plans: any[]; enforced: boolean; paidPlansAvailable: boolean }>("/admin/plans"); },

  async adminSetPaidPlansAvailable(enabled: boolean) {
    return req<{ ok: boolean; paidPlansAvailable: boolean }>("/admin/plans-availability", { method: "POST", body: JSON.stringify({ enabled }) });
  },

  async adminSetPlansEnforced(enabled: boolean) { return req<{ ok: boolean; enforced: boolean }>("/admin/plans-enforced", { method: "POST", body: JSON.stringify({ enabled }) }); },

  async adminSavePlan(body: any) { return req<{ ok: boolean; plan: any }>("/admin/plans", { method: "POST", body: JSON.stringify(body) }); },

  async adminUpdatePlan(id: string, patch: any) { return req<{ ok: boolean; plan: any }>(`/admin/plans/${id}`, { method: "PATCH", body: JSON.stringify(patch) }); },

  // v3.9.42 — plan subscriptions + billing view
  async myPlan() { return req<{ sections: any[]; usage: any; paidPlansAvailable: boolean }>("/billing/my-plan"); },

  async subscribePlan(body: { planKey: string; method?: "qrph" | "card"; mobile?: string }) {
    return req<any>("/billing/subscribe", { method: "POST", body: JSON.stringify(body) });
  },

  // v3.9.48 — listing boosts (paid placement)
  async myBoosts(signal?: AbortSignal) { return req<{ boosts: { postType: string; postId: string; until: string }[]; paidPlansAvailable: boolean }>("/billing/boosts", { signal }, { rejectUnauthorized: signal !== undefined }); },

  async boostListing(body: { planKey: string; postType: string; postId: string; method?: "qrph" | "card"; mobile?: string }) {
    return req<any>("/billing/boost", { method: "POST", body: JSON.stringify(body) });
  },

  async adminLedger(opts?: { limit?: number; offset?: number; kind?: string }) {
    const sp = new URLSearchParams();
    if (opts?.limit) sp.set("limit", String(opts.limit));
    if (opts?.offset) sp.set("offset", String(opts.offset));
    if (opts?.kind) sp.set("kind", opts.kind);
    const qs = sp.toString();
    return req<{ entries: any[]; total: number; platformRevenue: number; limit: number; offset: number }>(`/admin/ledger${qs ? `?${qs}` : ""}`);
  },

  // Increment 10: wallet balance + GMPay cash-in/out
  async getWallet(signal?: AbortSignal) {
    return req<{
      balance: number; available: number; held: number; pendingIn: number;
      canCashIn: boolean; canCashOut: boolean; provider: string; gmpayLive: boolean; gmpaySimulation: boolean;
      limits: { min: number; max: number }; transactions: any[];
    }>("/wallet", { signal }, { rejectUnauthorized: signal !== undefined });
  },

  async cashIn(amount: number, method: "qrph" | "card", mobile?: string) {
    return req<{ status: string; method: string; qrRaw?: string; checkoutUrl?: string; referenceNumber?: string; expiresInMinutes?: number; txnRef: string; balance: number; available: number }>(
      "/wallet/cashin", { method: "POST", body: JSON.stringify({ amount, method, mobile }) });
  },

  async cashOut(amount: number, receiver: { bankCode: string; bankName: string; accountNumber: string; receiverName: string }) {
    return req<{ status: string; txnRef: string; balance: number; available: number }>(
      "/wallet/cashout", { method: "POST", body: JSON.stringify({ amount, ...receiver }) });
  },

  async walletChannels() {
    return req<{ channels: { code: string; name: string }[] }>("/wallet/channels");
  },

  // Increment 22: vendor credit
  async getCredit(signal?: AbortSignal) {
    return req<{ limit: number; outstanding: number; available: number; transactions: any[] }>("/credit", { signal }, { rejectUnauthorized: signal !== undefined });
  },

  async repayCredit(amount: number) {
    return req<{ ok: boolean; repaid: number; limit: number; outstanding: number; available: number }>("/credit/repay", { method: "POST", body: JSON.stringify({ amount }) });
  },

  async adminCredit(q?: string) {
    const r = await req<{ accounts: any[] }>(`/admin/credit${q ? `?q=${encodeURIComponent(q)}` : ""}`);
    return r?.accounts ?? [];
  }
};
